import { Card } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Button } from "@shadcn-in-nx/ui/button";
import { CheckCircle, Clock, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const LATAM_HOURLY_RATE = 75;

export function SupportPlanCard({ plan, isCurrentPlan, hasSubscription, isCreatingCheckout, onPurchase, isYearly, isLatam }) {
    const { t } = useTranslation();
    const isPopular = plan.tier === "STANDARD_8H";
    const cardClass = isPopular
        ? "border-gray-900 relative shadow-lg"
        : isCurrentPlan
        ? "border-green-500 bg-green-50/30 relative"
        : "border-border/50 hover:border-border hover:shadow-md";

    const basePrice = isLatam ? plan.hours * LATAM_HOURLY_RATE : plan.price;
    const monthlyPrice = basePrice;
    const yearlyMonthlyPrice = Math.round(basePrice * 0.8);
    const displayPrice = isYearly ? yearlyMonthlyPrice : monthlyPrice;
    const hourlyRate = isLatam ? LATAM_HOURLY_RATE : plan.hourlyRate;

    return (
        <Card className={`p-6 border-2 transition-all ${cardClass}`}>
            {isPopular && !isCurrentPlan && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gray-900 text-white">
                    {t("billing.supportPlans.mostPopular")}
                </Badge>
            )}
            {isCurrentPlan && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-600 text-white">
                    {t("billing.supportPlans.currentPlan")}
                </Badge>
            )}
            <div className="text-center mb-6">
                <h4 className="text-lg font-semibold mb-1">{plan.name}</h4>
                <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                <div className="mb-2">
                    <span className="text-4xl font-bold">${displayPrice}</span>
                    <span className="text-muted-foreground">{t("billing.supportPlans.perMonth")}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                    {isYearly ? t("billing.supportPlans.billedYearly", { amount: yearlyMonthlyPrice * 12 }) : t("billing.supportPlans.perHour", { rate: hourlyRate })}
                </p>
            </div>
            <div className="space-y-3 mb-6">
                {plan.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                    </div>
                ))}
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-4">
                <Clock className="w-4 h-4" />
                <span>{t("billing.supportPlans.hoursPerMonth", { hours: plan.hours })}</span>
            </div>
            <Button
                onClick={() => onPurchase(plan.tier)}
                disabled={isCreatingCheckout || isCurrentPlan}
                className={`w-full ${isPopular && !isCurrentPlan ? "bg-gray-900 hover:bg-gray-800 text-white" : ""}`}
                variant={isCurrentPlan ? "outline" : isPopular ? "default" : "outline"}
            >
                {isCreatingCheckout ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : isCurrentPlan ? (
                    t("billing.supportPlans.currentPlan")
                ) : hasSubscription ? (
                    t("billing.supportPlans.upgrade")
                ) : (
                    t("billing.supportPlans.getStarted")
                )}
            </Button>
            {!isCurrentPlan && (
                <p className="text-xs text-center text-muted-foreground mt-2">
                    {t("billing.supportPlans.cancelAnytime")}
                </p>
            )}
        </Card>
    );
}
